import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

function cleanWbStateFromUrlString(input: string): string {
  if (!input || typeof input !== 'string' || !input.includes('wb_state')) return input;
  try {
    const url = new URL(input, window.location.origin);
    if (!url.searchParams.has('wb_state')) return input;
    url.searchParams.delete('wb_state');
    const qs = url.searchParams.toString();
    const sameOrigin = url.origin === window.location.origin;
    const path = url.pathname + (qs ? `?${qs}` : '') + url.hash;
    return sameOrigin ? path : url.toString();
  } catch {
    return input;
  }
}

function stripWbStateFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (url.searchParams.has('wb_state')) {
    url.searchParams.delete('wb_state');
    const clean = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState(window.history.state, '', clean);
  }
}

// Patch history methods once so any widget write that re-appends wb_state is stripped immediately.
if (typeof window !== 'undefined' && !(window as any).__wbStateHistoryPatched) {
  (window as any).__wbStateHistoryPatched = true;
  const origPush = window.history.pushState.bind(window.history);
  const origReplace = window.history.replaceState.bind(window.history);
  window.history.pushState = function (data: any, unused: string, url?: string | URL | null) {
    const next = url == null ? url : cleanWbStateFromUrlString(typeof url === 'string' ? url : url.toString());
    return origPush(data, unused, next as any);
  } as typeof window.history.pushState;
  window.history.replaceState = function (data: any, unused: string, url?: string | URL | null) {
    const next = url == null ? url : cleanWbStateFromUrlString(typeof url === 'string' ? url : url.toString());
    return origReplace(data, unused, next as any);
  } as typeof window.history.replaceState;
  // Catch any lingering param (e.g. set before patch, or via popstate/back-forward cache).
  window.addEventListener('popstate', () => stripWbStateFromUrl());
  window.addEventListener('pageshow', () => stripWbStateFromUrl());
  stripWbStateFromUrl();
}

function StripWbState() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    stripWbStateFromUrl();
  }, [pathname, search, hash]);
  return null;
}

function ScrollToTopOnNav() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}
import Layout from "@/components/Layout";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import AnalyticsDebugPanel from "@/components/AnalyticsDebugPanel";
import AnalyticsQAChecklist from "@/components/AnalyticsQAChecklist";
import WidgetDiagnosticsPanel from "@/components/WidgetDiagnosticsPanel";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Rideshare = lazy(() => import("./pages/Rideshare"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AnalyticsVerify = lazy(() => import("./pages/AnalyticsVerify"));
const AnalyticsHandoffDocs = lazy(() => import("./pages/AnalyticsHandoffDocs"));
const AnalyticsHandoffTest = lazy(() => import("./pages/AnalyticsHandoffTest"));
const IndexingMonitor = lazy(() => import("./pages/IndexingMonitor"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <StripWbState />
        <ScrollToTopOnNav />
        <AnalyticsTracker />
        <AnalyticsDebugPanel />
        <AnalyticsQAChecklist />
        <WidgetDiagnosticsPanel />
        <Layout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              
              <Route path="/rideshare" element={<Rideshare />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/analytics/verify" element={<AnalyticsVerify />} />
              <Route path="/analytics/handoff-docs" element={<AnalyticsHandoffDocs />} />
              <Route path="/analytics/handoff-test" element={<AnalyticsHandoffTest />} />
              <Route path="/admin/indexing" element={<IndexingMonitor />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
